
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleDollarSign, Loader2, CalendarIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { toast } from '@/components/ui/sonner';
import { createSubscription } from '@/pages/admin/rentals/services/subscriptionCreate';
import { Subscription } from '@/services/types/subscriptionTypes';
import { calculateNextPaymentDate, formatDateForApi } from '@/utils/dateUtils';

// Схема валидации для формы создания подписки
const formSchema = z.object({
  amount: z.coerce.number().min(1, { message: 'Сумма должна быть больше 0' }),
  interval: z.enum(['day', 'week', 'month', 'year'], {
    required_error: 'Выберите периодичность',
  }),
  start_date: z.string().optional(),
  card_first6: z.string().max(6).optional(),
  card_last4: z.string().max(4).optional(),
  card_expiry_month: z.string().max(2).optional(),
  card_expiry_year: z.string().max(4).optional(),
  card_type: z.string().optional(),
});

// Типы данных формы
type FormData = z.infer<typeof formSchema>;

interface AddSubscriptionFormProps {
  rentalId: string;
  onSubscriptionAdded: () => void;
  existingSubscriptions?: Subscription[];
}

export const AddSubscriptionForm: React.FC<AddSubscriptionFormProps> = ({ 
  rentalId, 
  onSubscriptionAdded,
  existingSubscriptions = []
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Проверяем, есть ли активные подписки
  const hasActiveSubscription = existingSubscriptions.some(sub => sub.status === 'active');
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
      interval: 'month',
      start_date: formatDateForApi(new Date()),
    },
  });
  
  // Рассчитываем дату следующего платежа при изменении интервала или даты начала
  const calculateNextPaymentDatePreview = (startDate: string, interval: 'day' | 'week' | 'month' | 'year') => {
    const nextDate = calculateNextPaymentDate(startDate, interval);
    return formatDateForApi(nextDate);
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      // Преобразуем данные для создания подписки
      const subscriptionData = {
        rental_id: rentalId,
        interval: data.interval,
        amount: data.amount,
        start_date: data.start_date,
        card_first6: data.card_first6 || undefined,
        card_last4: data.card_last4 || undefined,
        card_expiry_month: data.card_expiry_month || undefined,
        card_expiry_year: data.card_expiry_year || undefined,
        card_type: data.card_type || undefined,
      };
      
      // Добавим автоматический расчет даты следующего платежа
      const nextPaymentDate = calculateNextPaymentDate(data.start_date, data.interval);
      const nextPaymentDateString = formatDateForApi(nextPaymentDate);
      console.log(`Рассчитанная дата следующего платежа: ${nextPaymentDateString}`);
      
      const { data: result, error, success } = await createSubscription(subscriptionData);
      
      if (success && result) {
        toast.success('Подписка успешно создана');
        setOpen(false);
        form.reset({
          amount: 0,
          interval: 'month',
          start_date: formatDateForApi(new Date()),
          card_first6: '',
          card_last4: '',
          card_expiry_month: '',
          card_expiry_year: '',
          card_type: '',
        });
        onSubscriptionAdded();
      } else {
        toast.error(`Ошибка при создании подписки: ${error}`);
      }
    } catch (err: any) {
      console.error('Ошибка при создании подписки:', err);
      toast.error('Произошла ошибка при создании подписки');
    } finally {
      setLoading(false);
    }
  };
  
  // Предупреждение о существующей активной подписке
  const ActiveSubscriptionWarning = () => {
    if (!hasActiveSubscription) return null;
    
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-800">
        <strong>Внимание!</strong> Для этой аренды уже существует активная подписка. 
        Создание новой активной подписки может привести к дублированию платежей.
      </div>
    );
  };
  
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-medium">Подписки</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="flex items-center gap-1">
              <CircleDollarSign className="h-4 w-4" />
              Создать подписку
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Создание новой подписки</DialogTitle>
            </DialogHeader>
            
            <ActiveSubscriptionWarning />
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Сумма платежа (₽)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="Укажите сумму" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="interval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Периодичность платежа</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите периодичность" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="day">Ежедневно</SelectItem>
                          <SelectItem value="week">Еженедельно</SelectItem>
                          <SelectItem value="month">Ежемесячно</SelectItem>
                          <SelectItem value="year">Ежегодно</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        Дата начала
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        Первый платеж будет: {calculateNextPaymentDatePreview(
                          field.value || formatDateForApi(new Date()),
                          form.watch('interval')
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="bg-muted/30 p-3 rounded-lg border">
                  <h4 className="text-sm font-medium mb-2">Данные платежной карты (опционально)</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <FormField
                      control={form.control}
                      name="card_first6"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Первые 6 цифр</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="XXXXXX" 
                              maxLength={6}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_last4"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Последние 4 цифры</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="XXXX" 
                              maxLength={4}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    <FormField
                      control={form.control}
                      name="card_expiry_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Месяц</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="MM" 
                              maxLength={2}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_expiry_year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Год</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="YYYY" 
                              maxLength={4}
                              {...field} 
                              value={field.value || ''} 
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="card_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Тип карты</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value || ''}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Тип" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="">Не указан</SelectItem>
                              <SelectItem value="Visa">Visa</SelectItem>
                              <SelectItem value="MasterCard">MasterCard</SelectItem>
                              <SelectItem value="Mir">Мир</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" type="button" onClick={() => setOpen(false)} disabled={loading}>
                    Отмена
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Создать подписку
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
